/**
 * Copyright (c) 2016-present GlossGenius. All rights reserved.
 */

import React from 'react';

import cx from 'classnames';
import PropTypes from 'prop-types';

import s from './Carousel.module.css';

const DOT_CONTROL_MAX_SLIDES = 10;

class Carousel extends React.Component {
  static propTypes = {
    className: PropTypes.string,
    slideClassName: PropTypes.string,
    controlsClassName: PropTypes.string,
    children: PropTypes.node.isRequired,
    carouselId: PropTypes.string,
  };

  constructor(props) {
    super(props);

    this.state = {
      slide: 0,
      sliding: false,
      offset: 0,
    };
  }

  componentDidUpdate(prevProps) {
    if (this.props.carouselId !== prevProps.carouselId) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ slide: 0 });
    }
  }

  setInitialOffset() {
    if (this.state.offset !== 0) {
      this.setState({ offset: 0, sliding: true }, this.setTransitionTimer);
    }
  }

  setTransitionTimer() {
    window.clearTimeout(this.transitionTimer);
    this.transitionTimer = window.setTimeout(this.handleTransitionEnd, 400);
  }

  handleTouchStart = (event) => {
    if (this.state.sliding) {
      return;
    }
    if (event.touches) {
      this.dragging = true;
      this.checkDirection = true;
      this.startTime = Date.now();
      this.startX = this.lastX = event.touches[0].pageX;
      this.startY = event.touches[0].pageY;
      this.lastShiftX = 0;
    }

    this.setInitialOffset();
  };

  handleTouchMove = (event) => {
    if (!this.dragging || this.state.sliding || !event.touches) {
      return;
    }
    const x = event.touches[0].pageX;
    const y = event.touches[0].pageY;
    const shiftX = x - this.startX;
    const shiftY = y - this.startY;

    this.lastShiftX = x - this.lastX;
    this.lastX = x;

    if (this.checkDirection) {
      this.checkDirection = false;
      if (Math.abs(shiftY) > Math.abs(shiftX)) {
        this.dragging = false;
        return;
      }
    }

    event.preventDefault();
    const { slide } = this.state;
    const count = React.Children.count(this.props.children);

    let next = shiftX > 0 ? slide - 1 : slide + 1;

    if (next >= count) {
      next = 0;
    }
    if (next < 0) {
      next = count - 1;
    }
    this.setState({ offset: shiftX, next });
  };

  handleTouchEnd = () => {
    if (!this.dragging || this.checkDirection) {
      return;
    }
    this.dragging = false;
    const { slide, offset } = this.state;
    const shiftX = this.lastX - this.startX;
    const dx = Math.abs(shiftX);

    // eslint-disable-next-line sonarjs/no-collapsible-if
    if (dx > 50 || (this.startTime > Date.now() - 200 && dx > 3)) {
      if (shiftX * this.lastShiftX >= 0) {
        const nextSlide = offset > 0 ? slide - 1 : slide + 1;

        this.changeSlide(nextSlide);
        return;
      }
    }

    this.setInitialOffset();
  };

  handleTransitionEnd = () => {
    this.setState({ sliding: false, offset: 0 });
  };

  handleControlClick = (event) => {
    this.changeSlide(Number(event.currentTarget.dataset.index));
  };

  changeSlide(nextSlide) {
    const count = React.Children.count(this.props.children);

    let slide = nextSlide;

    if (slide >= count) {
      slide = count - 1;
    }
    if (slide < 0) {
      slide = 0;
    }
    this.setState({ slide, sliding: true }, this.setTransitionTimer);
  }

  renderControls = (slides) => {
    const total = React.Children.count(slides);

    if (total > DOT_CONTROL_MAX_SLIDES) {
      return (
        <div key={this.state.slide}>
          {this.state.slide + 1}/{total}
        </div>
      );
    }

    return React.Children.map(slides, (item, index) => (
      <button
        key={item.key}
        type="button"
        className={cx(
          s.control,
          this.state.slide === index && s.controlCurrent,
        )}
        data-index={index}
        onClick={this.handleControlClick}
      >
        {index + 1}
      </button>
    ));
  };

  render = () => {
    const { controlsClassName, className, slideClassName, children } =
      this.props;
    const { slide, sliding, offset } = this.state;
    const transform =
      this.dragging && offset !== 0
        ? `translateX(calc(${offset}px - ${slide * 100}%))`
        : `translateX(-${slide * 100}%)`;
    const slides = React.Children.map(children, (child, index) =>
      React.cloneElement(child, { key: `${child.key || index}-clone` }),
    );

    return (
      <div
        className={cx(s.root, className)}
        onTouchStart={this.handleTouchStart}
        onTouchMove={this.handleTouchMove}
        onTouchEnd={this.handleTouchEnd}
        onTouchCancel={this.handleTouchEnd}
      >
        <ul
          ref={(ref) => (this.root = ref)}
          className={cx(s.slider, sliding && s.sliding)}
          style={{ transform, msTransform: transform }}
        >
          {React.Children.map(slides, (item) => (
            <li className={cx(s.slide, slideClassName)} key={item.key}>
              {item}
            </li>
          ))}
        </ul>
        <div className={cx(s.controls, controlsClassName)}>
          {this.renderControls(slides)}
        </div>
      </div>
    );
  };
}

export default Carousel;
